/**
 * Small rewards for deciding. A love bursts into gold stars and a star flies into the shortlist; a
 * like puffs hearts where the card left and a heart flies after it; a pass leaves a soft puff.
 * The button that did it bounces, and a word marks the milestones.
 */
import type { Verdict } from "../data/types";
import { decisions, haptic, settings, toast } from "../state/app";
import { openOverlay } from "../state/router";
import { burst, confetti } from "./confetti";
import { maybeAskForReview } from "../state/review";

export interface Tally {
  seen: number;
  liked: number;
  loved: number;
}

export function tally(): Tally {
  const t = { seen: 0, liked: 0, loved: 0 };
  for (const d of Object.values(decisions.get().map)) {
    t.seen++;
    if (d.d === 1) t.liked++;
    else if (d.d === 2) t.loved++;
  }
  return t;
}

const SHORTLIST_STEPS = [5, 10, 25, 50, 100, 200, 500];
const SEEN_STEPS = [25, 50, 100, 250, 500, 1000, 2000, 3000, 5000, 10000];

const calm = () => settings.get().reduceMotion || matchMedia("(prefers-reduced-motion: reduce)").matches;
const token = (v: string, fallback: string) => getComputedStyle(document.documentElement).getPropertyValue(v).trim() || fallback;
const crossed = (steps: number[], before: number, after: number) => steps.filter((s) => before < s && s <= after).pop();

/** Restart an element's bounce (`.bump`), even mid-bounce. */
export function bump(el: Element | null | undefined): void {
  if (!el || calm()) return;
  el.classList.remove("bump");
  void (el as HTMLElement).offsetWidth;
  el.classList.add("bump");
}

type Point = { x: number; y: number };

function centre(el: Element | null | undefined, rise = 0.5): Point | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height * rise };
}

const HEART =
  '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-7.5-4.6-9.6-9.3C.9 8.4 3 4.5 6.7 4.5c2.2 0 3.6 1.2 4.3 2.4.7-1.2 2.1-2.4 4.3-2.4 3.7 0 5.8 3.9 4.3 7.2C19.5 16.4 12 21 12 21z"/></svg>';
const STAR =
  '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z"/></svg>';

/** A heart or star that arcs from where the card went into the Shortlist tab. */
function flyToShortlist(from: Point, kind: "heart" | "star"): void {
  const to = centre(document.querySelector('.tab[data-tab="list"] .tab-icon'));
  if (!to || calm() || typeof document.body.animate !== "function") return;
  const el = document.createElement("div");
  el.className = `fly fly-${kind}`;
  el.setAttribute("aria-hidden", "true");
  el.innerHTML = kind === "heart" ? HEART : STAR;
  document.body.appendChild(el);
  const at = (x: number, y: number, s: number) => `translate(${x}px, ${y}px) translate(-50%, -50%) scale(${s})`;
  const mid = { x: from.x + (to.x - from.x) * 0.35, y: Math.min(from.y, to.y) - 70 };
  el
    .animate(
      [
        { transform: at(from.x, from.y, 0.4), opacity: 0 },
        { transform: at(mid.x, mid.y, 1.25), opacity: 1, offset: 0.35 },
        { transform: at(to.x, to.y, 0.45), opacity: 0.9 },
      ],
      { duration: 620, easing: "cubic-bezier(.4,0,.5,1)" },
    )
    .addEventListener("finish", () => el.remove());
}

/**
 * Celebrate a decision just made. `from` is where the effect starts (the card or the button);
 * `before` is the tally before the decision. Returns true if it showed a message, so callers
 * can skip their own.
 */
export function cheer(
  verdict: Verdict,
  from: Element | Point | null | undefined,
  before: Tally,
  opts: { quiet?: boolean; fly?: boolean } = {},
): boolean {
  const after = tally();
  const at = from instanceof Element ? centre(from, verdict === 2 ? 0.4 : 0.5) : from ?? null;
  if (verdict === 2) {
    haptic([12, 30, 12, 30, 20]);
    if (at && !calm()) {
      burst(at.x, at.y, [token("--love", "#b98200"), "#f5c542", "#ffdf80"], { count: 26, reach: 150 });
      if (opts.fly !== false) flyToShortlist(at, "star");
    }
  } else if (verdict === 1) {
    if (at && !calm()) {
      burst(at.x, at.y, [token("--like", "#2f9a66"), "#7fd6a5", "#b8ebcd"], { count: 14, reach: 90, duration: 750, shape: "heart" });
      if (opts.fly !== false) flyToShortlist(at, "heart");
    }
  } else if (at && !calm()) {
    // a pass: a soft puff where the card went, nothing more
    burst(at.x, at.y, [token("--nope-soft", "#fbe5e7"), token("--line-2", "#d2c6ea"), token("--nope", "#d9505b")], {
      count: 12,
      reach: 60,
      duration: 550,
      shape: "dot",
    });
  }
  if (opts.quiet) return false;

  const shortBefore = before.liked + before.loved;
  const shortAfter = after.liked + after.loved;
  const party = (n: number) => {
    if (!calm()) confetti([token("--love", "#b98200"), token("--like", "#2f9a66"), token("--girl", "#d4628c"), token("--boy", "#3f7fcc")], { count: n });
  };
  if (verdict === 2 && before.loved === 0 && after.loved === 1) {
    toast("Your first love. It sits at the top of your shortlist.", undefined, 4200);
    return true;
  }
  if (shortBefore === 0 && shortAfter === 1) {
    toast("Your first like. It's waiting in your shortlist.", undefined, 4200);
    return true;
  }
  const step = crossed(SHORTLIST_STEPS, shortBefore, shortAfter);
  if (step) {
    if (step >= 25) party(70);
    if (step >= 25) maybeAskForReview();
    if (step === 10) toast("10 names on your shortlist. Rank them in a Name-off?", { label: "Name-off", run: () => openOverlay("nameoff") }, 6000);
    else toast(`${step} names on your shortlist`, undefined, 4200);
    return true;
  }
  const seen = crossed(SEEN_STEPS, before.seen, after.seen);
  if (seen) {
    if (seen >= 100) party(60);
    toast(`${seen.toLocaleString("en-GB")} names seen, ${shortAfter.toLocaleString("en-GB")} on your shortlist`, undefined, 4200);
    return true;
  }
  return false;
}
